import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  GlobeAltIcon,
  ChevronDownIcon,
  PlusIcon,
  CheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useOrganisation } from '../../contexts/OrganisationContext';
import apiService from '../../services/api';
import { Region, RegionCreate } from '../../types/api';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

const RegionSelector: React.FC = () => {
  const { t } = (useTranslation as any)('common');
  const navigate = useNavigate();
  const { currentOrganisation, selectedRegion, selectRegion } = useOrganisation();
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);

  // Create modal state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionDescription, setNewRegionDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const loadRegions = useCallback(async () => {
    if (!currentOrganisation) return;
    setLoading(true);
    try {
      const data = await apiService.getRegions(currentOrganisation.id);
      setRegions(data);
      // Auto-select default or first region
      const defaultRegion = data.find((r) => r.is_default) || data[0];
      if (defaultRegion && !selectedRegion) {
        selectRegion(defaultRegion);
      }
    } catch (error) {
      Logger.error('Failed to load regions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentOrganisation, selectedRegion]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const handleSelectRegion = (region: Region) => {
    selectRegion(region);
  };

  const handleCreateRegion = async () => {
    if (!currentOrganisation || !newRegionName.trim()) return;
    setCreating(true);
    try {
      const created = await apiService.createRegion(currentOrganisation.id, {
        name: newRegionName.trim(),
        description: newRegionDescription.trim() || undefined,
      });
      setRegions((prev) => [...prev, created]);
      selectRegion(created);
      setShowCreateForm(false);
      setNewRegionName('');
      setNewRegionDescription('');
    } catch (error) {
      Logger.error('Failed to create region:', error);
    } finally {
      setCreating(false);
    }
  };

  const globalRegions = regions.filter((r) => r.type === 'global');
  const customRegions = regions.filter((r) => r.type === 'custom');

  // Regions coming soon (not yet available)
  const comingSoonRegions = [
    { name: 'Africa Central', slug: 'africa-central-1', flag: '🇨🇲' },
    { name: 'Africa West', slug: 'africa-west-1', flag: '🇨🇮' },
  ];

  if (loading && regions.length === 0) {
    return null;
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-text-secondary hover:bg-surface-3 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:ring-offset-0">
        <GlobeAltIcon className="w-4 h-4 text-text-quaternary" />
        <span className="hidden sm:inline">
          {selectedRegion ? selectedRegion.name : t('region.region') as string}
        </span>
        {selectedRegion && (
          <span className="text-[10px] text-text-quaternary hidden md:inline">
            ({selectedRegion.slug})
          </span>
        )}
        <ChevronDownIcon className="w-3.5 h-3.5 text-text-quaternary" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-1.5 w-72 origin-top-right rounded-card bg-surface-2 border border-border-1 shadow-card-hover focus:outline-none overflow-hidden">
          {/* Header */}
          <div className="px-4 py-2.5 border-b border-border-0">
            <p className="text-[11px] text-text-quaternary uppercase font-semibold tracking-wide">
              {t('region.deploymentRegion')}
            </p>
          </div>

          {/* Global Regions */}
          {globalRegions.length > 0 && (
            <div>
              <div className="px-4 py-1.5">
                <p className="text-[10px] text-text-quaternary uppercase font-semibold">
                  {t('region.nubicloudRegions')}
                </p>
              </div>
              {globalRegions.map((region) => (
                <Menu.Item key={region.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handleSelectRegion(region)}
                      className={`${
                        active ? 'bg-surface-3' : ''
                      } flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          region.is_configured ? 'bg-emerald-400' : 'bg-amber-400'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-medium text-text-primary truncate">
                            {region.name}
                          </p>
                          <p className="text-[10.5px] text-text-quaternary">
                            {region.slug}
                          </p>
                        </div>
                      </div>
                      {selectedRegion?.id === region.id && (
                        <CheckIcon className="w-4 h-4 text-accent-400 flex-shrink-0" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          )}

          {/* Custom Regions */}
          {customRegions.length > 0 && (
            <div className="border-t border-border-0">
              <div className="px-4 py-1.5">
                <p className="text-[10px] text-text-quaternary uppercase font-semibold">
                  {t('region.customRegions')}
                </p>
              </div>
              {customRegions.map((region) => (
                <Menu.Item key={region.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handleSelectRegion(region)}
                      className={`${
                        active ? 'bg-surface-3' : ''
                      } flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          region.is_configured ? 'bg-emerald-400' : 'bg-amber-400'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-medium text-text-primary truncate">
                            {region.name}
                          </p>
                          <p className="text-[10.5px] text-text-quaternary">
                            {region.slug}
                          </p>
                        </div>
                      </div>
                      {selectedRegion?.id === region.id && (
                        <CheckIcon className="w-4 h-4 text-accent-400 flex-shrink-0" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          )}

          {/* Coming Soon Regions */}
          <div className="border-t border-border-0">
            <div className="px-4 py-1.5">
              <p className="text-[10px] text-text-quaternary uppercase font-semibold">
                {t('region.comingSoon')}
              </p>
            </div>
            {comingSoonRegions.map((region) => (
              <div
                key={region.slug}
                className="flex items-center justify-between w-full px-4 py-2.5 opacity-60 cursor-default"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-sm flex-shrink-0">{region.flag}</span>
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-text-primary truncate">
                      {region.name}
                    </p>
                    <p className="text-[10.5px] text-text-quaternary">
                      {region.slug}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 flex-shrink-0">
                  <ClockIcon className="w-3 h-3" />
                  {t('region.soon')}
                </span>
              </div>
            ))}
          </div>

          {/* Create Region inline form */}
          {showCreateForm ? (
            <div className="border-t border-border-0 p-3 space-y-2">
              <input
                type="text"
                placeholder={t('region.regionName') as string}
                value={newRegionName}
                onChange={(e) => setNewRegionName(e.target.value)}
                className="w-full px-2.5 py-1.5 text-[12px] rounded-md border border-border-1 bg-surface-1 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateRegion();
                  if (e.key === 'Escape') setShowCreateForm(false);
                }}
              />
              <input
                type="text"
                placeholder={t('region.descriptionOptional') as string}
                value={newRegionDescription}
                onChange={(e) => setNewRegionDescription(e.target.value)}
                className="w-full px-2.5 py-1.5 text-[12px] rounded-md border border-border-1 bg-surface-1 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500/40"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateRegion}
                  disabled={!newRegionName.trim() || creating}
                  className="flex-1 px-2.5 py-1.5 text-[11px] font-medium text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50 rounded-md transition-colors"
                >
                  {creating ? t('region.creating') as string : t('common:create') as string}
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewRegionName('');
                    setNewRegionDescription('');
                  }}
                  className="px-2.5 py-1.5 text-[11px] font-medium text-text-secondary hover:bg-surface-3 rounded-md transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-border-0">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowCreateForm(true);
                    }}
                    className={`${
                      active ? 'bg-surface-3' : ''
                    } flex items-center w-full px-4 py-2.5 text-[12px] text-accent-400 font-medium transition-colors`}
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {t('region.addCustomRegion')}
                  </button>
                )}
              </Menu.Item>
              {currentOrganisation && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => navigate(`/organisations/${currentOrganisation.id}/settings`)}
                      className={`${
                        active ? 'bg-surface-3' : ''
                      } flex items-center w-full px-4 py-2.5 text-[12px] text-text-tertiary transition-colors`}
                    >
                      {t('region.manageRegions')}
                    </button>
                  )}
                </Menu.Item>
              )}
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default RegionSelector;
